import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Globe, Loader, Check, X, Trash2, ArrowLeft, Sparkles, Coins, Search, ArrowUpDown, Archive, ArchiveRestore, Database, Inbox, ChevronDown, ChevronRight } from "lucide-react";
import { formatCost, formatEur } from "./cost";
import PageHeader from "./PageHeader";
import { navigate, useSegmente } from "./router";
import KarriereBloecke, { QuellenLeiste } from "./KarriereBloecke";

const STATUS_LABEL = {
  entwurf: "Entwurf",
  in_bearbeitung: "In Bearbeitung",
  warten_partner: "Beim Partner",
  abgeschlossen: "Abgeschlossen",
};

// Wie bei den Stellenanzeigen: exakter gespeicherter Betrag, sonst Schaetzung aus Tokens.
function kosten(s) {
  return Number(s.cost_eur) > 0 ? formatEur(Number(s.cost_eur)) : formatCost(parseInt(s.total_tokens, 10) || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Sagt, woher die Angaben stammen - damit nachvollziehbar bleibt, worauf der
// Text fusst.
//
// Der Bericht entsteht erst, wenn der Datencheck oder die Merkmalssuche
// gelaufen ist. Vorher stand hier "ohne Website-Scan · keine Jotform-Daten" -
// das liest sich wie ein Befund, war aber nur die Abwesenheit einer Pruefung.
// Und "jotformStellen" gab es im Bericht nie: das Feld heisst "jotform".
function quellenText(seite) {
  const q = (seite.agent_feedback && seite.agent_feedback.quellen) || null;
  const teile = [];
  if (seite.standort) teile.push(seite.standort);

  if (!q) {
    teile.push("Quellen noch nicht geprüft – starte den Datencheck");
    return teile.join(" · ");
  }

  const gefunden = [];
  if (q.briefing) gefunden.push("Karriereportal-Formular");
  if (q.website > 0) gefunden.push(q.website === 1 ? "Website" : `${q.website} Websites`);
  if (q.social > 0) gefunden.push(q.social === 1 ? "Social Media" : `${q.social} Social-Media-Profile`);
  if (q.text > 0) gefunden.push("eigene Notizen");
  if (q.jotform > 0) gefunden.push(`${q.jotform} Formular-Einreichung${q.jotform === 1 ? "" : "en"}`);

  teile.push(gefunden.length ? `Quellen: ${gefunden.join(", ")}` : "keine verwertbaren Quellen gefunden");
  if ((q.fehler || []).length) {
    teile.push(`${q.fehler.length} Quelle${q.fehler.length === 1 ? "" : "n"} nicht erreichbar`);
  }
  return teile.join(" · ");
}

export default function KarriereportalPage() {
  const [partners, setPartners] = useState([]);
  const [portale, setPortale] = useState([]);
  const [jfCounts, setJfCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [suche, setSuche] = useState("");
  const [sortierung, setSortierung] = useState("date-desc");
  const [zeigeAbgeschlossene, setZeigeAbgeschlossene] = useState(false);
  const [zeigeQuellen, setZeigeQuellen] = useState(false);
  const [neuOffen, setNeuOffen] = useState(false);
  // Adresse ist die Wahrheit: /careers · /careers/:partnerId · /careers/:partnerId/:portalId
  const segmente = useSegmente();
  const partnerIdAusPfad = segmente[0] === "careers" ? segmente[1] || null : null;

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch("/api/partners", { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
      fetch("/api/karriereseiten", { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
      fetch("/api/partners/jotform-counts", { credentials: "include" }).then((r) => (r.ok ? r.json() : {})),
    ])
      .then(([p, k, j]) => {
        setPartners(Array.isArray(p) ? p : []);
        setPortale(Array.isArray(k) ? k : []);
        setJfCounts(j && typeof j === "object" ? j : {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Portale je Partner buendeln - spart einen eigenen Endpunkt.
  const proPartner = useMemo(() => {
    const map = {};
    for (const k of portale) {
      if (!map[k.partner_id]) map[k.partner_id] = { anzahl: 0, kosten: 0, letztes: null, hinweise: 0 };
      const e = map[k.partner_id];
      e.anzahl += 1;
      e.kosten += Number(k.cost_eur) || 0;
      e.hinweise += Number(k.offene_hinweise) || 0;
      const d = k.updated_at || k.created_at;
      if (d && (!e.letztes || new Date(d) > new Date(e.letztes))) e.letztes = d;
    }
    return map;
  }, [portale]);

  const gefiltert = useMemo(() => {
    const liste = partners.filter((p) => {
      // Nur Partner zeigen, fuer die es ein Portal gibt. Wer neu anfangen will,
      // nutzt "Neues Portal" und waehlt den Partner dort.
      if (!proPartner[p.partner_id]) return false;
      const abgeschlossen = p.archived === true || p.archived === "true";
      if (zeigeAbgeschlossene !== abgeschlossen) return false;
      if (suche.trim()) return (p.unternehmen || "").toLowerCase().includes(suche.toLowerCase());
      return true;
    });
    const letztes = (p) => (proPartner[p.partner_id] || {}).letztes || p.last_generated;
    switch (sortierung) {
      case "name-asc": liste.sort((a, b) => a.unternehmen.localeCompare(b.unternehmen, "de")); break;
      case "name-desc": liste.sort((a, b) => b.unternehmen.localeCompare(a.unternehmen, "de")); break;
      case "portale-desc":
        liste.sort((a, b) => ((proPartner[b.partner_id] || {}).anzahl || 0) - ((proPartner[a.partner_id] || {}).anzahl || 0));
        break;
      case "date-asc": liste.sort((a, b) => new Date(letztes(a)) - new Date(letztes(b))); break;
      case "date-desc":
      default: liste.sort((a, b) => new Date(letztes(b)) - new Date(letztes(a))); break;
    }
    return liste;
  }, [partners, proPartner, suche, sortierung, zeigeAbgeschlossene]);

  // Abschliessen wie bei den Stellenanzeigen. Es ist derselbe Partner und
  // damit dasselbe Kennzeichen: wer hier abgeschlossen wird, ist es auch dort.
  const abschliessen = async (ev, partner) => {
    ev.stopPropagation();
    const istAbgeschlossen = partner.archived === true || partner.archived === "true";
    const was = istAbgeschlossen ? "wiederherstellen" : "abschließen";
    if (!window.confirm(`„${partner.unternehmen}" wirklich ${was}?`)) return;
    try {
      const res = await fetch(`/api/partners/${encodeURIComponent(partner.partner_id)}/archive`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ archived: !istAbgeschlossen }),
      });
      if (!res.ok) throw new Error("Fehlgeschlagen");
      load();
    } catch (err) {
      console.error("Abschließen:", err);
    }
  };

  // Nur Partner zaehlen, die hier ueberhaupt sichtbar waeren - also solche mit
  // Portal. Sonst stand hier die Zahl aller abgeschlossenen Partner aus den
  // Stellenanzeigen, und der Filter fuehrte auf eine leere Seite.
  const abgeschlosseneAnzahl = partners.filter(
    (p) => (p.archived === true || p.archived === "true") && proPartner[p.partner_id]
  ).length;
  const portalSumme = portale.length;
  const kostenSumme = portale.reduce((s, k) => s + (Number(k.cost_eur) || 0), 0);

  if (partnerIdAusPfad) {
    const p = partners.find((x) => x.partner_id === partnerIdAusPfad);
    // Beim Direktaufruf sind die Partner evtl. noch nicht geladen.
    if (loading) return <div className="loading-indicator"><div className="spinner" /><span>Lade …</span></div>;
    if (!p) {
      return (
        <div className="empty-card card">
          <Globe size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>Diesen Partner gibt es nicht (mehr).</p>
          <button className="btn-secondary" style={{ marginTop: 16 }} onClick={() => navigate("/careers")}>
            <ArrowLeft size={16} /> Zur Übersicht
          </button>
        </div>
      );
    }
    return <PartnerPortale partner={p} onBack={() => { navigate("/careers"); load(); }} />;
  }

  return (
    <>
      <PageHeader
        eyebrow="Texte"
        title="Karriereportal"
        subtitle="Übersicht aller Partner und ihrer generierten Karriereportal-Texte."
        actions={
          <>
            <button
              className="icon-btn"
              onClick={() => navigate("/papierkorb/karriereportal")}
              data-tooltip="Papierkorb"
              aria-label="Papierkorb"
            >
              <Trash2 size={16} />
            </button>
            <button className="btn-primary" onClick={() => setNeuOffen(true)}>
              <Plus size={16} /> Neues Projekt
            </button>
          </>
        }
      />

      {!loading && partners.length > 0 && (
        <div className="toolbar">
          <div className="search-input-wrap toolbar-search">
            <Search size={16} />
            <input
              type="text"
              className="search-input"
              placeholder="Partner suchen …"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
            />
            {suche && <button className="search-clear" onClick={() => setSuche("")}>&times;</button>}
          </div>

          <div className="toolbar-select">
            <ArrowUpDown size={14} />
            <select className="sort-select" value={sortierung} aria-label="Sortierung"
              onChange={(e) => setSortierung(e.target.value)}>
              <option value="date-desc">Neueste zuerst</option>
              <option value="date-asc">Älteste zuerst</option>
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
              <option value="portale-desc">Meiste Portale</option>
            </select>
          </div>

          {abgeschlosseneAnzahl > 0 && (
            <button className={`btn-secondary ${zeigeAbgeschlossene ? "active" : ""}`}
              onClick={() => setZeigeAbgeschlossene(!zeigeAbgeschlossene)}>
              <Archive size={14} /> Abgeschlossene ({abgeschlosseneAnzahl})
            </button>
          )}

          <button className="btn-secondary" onClick={() => setZeigeQuellen(true)}>
            <Database size={14} /> Datenquellen
          </button>
        </div>
      )}

      {!loading && gefiltert.length > 0 && (
        <p className="page-counter">
          <strong>{gefiltert.length}</strong> Partner mit Portal · <strong>{portalSumme}</strong> Version
          {portalSumme !== 1 ? "en" : ""} insgesamt · Gesamtkosten: <strong>{formatEur(kostenSumme)}</strong>
        </p>
      )}

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade Partner …</span></div>
      ) : gefiltert.length === 0 ? (
        <div className="empty-card card">
          <Globe size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>
            {suche
              ? "Kein Partner mit Karriereportal gefunden."
              : zeigeAbgeschlossene
                ? "Keine abgeschlossenen Partner mit Portal."
                : "Noch kein Karriereportal angelegt. Starte mit „Neues Portal“ – die Texte entstehen aus den Jotform-Formularen und der Website des Partners."}
          </p>
        </div>
      ) : (
        <div className="cards-grid">
          {gefiltert.map((p) => {
            const e = proPartner[p.partner_id] || { anzahl: 0, kosten: 0, letztes: null, hinweise: 0 };
            const abgeschlossen = p.archived === true || p.archived === "true";
            return (
              <div key={p.partner_id} role="button" tabIndex={0}
                className={`tk-card ${abgeschlossen ? "tk-card--archived" : ""}`}
                onClick={() => navigate(`/careers/${p.partner_id}`)}
                onKeyDown={(ev) => { if (ev.key === "Enter") navigate(`/careers/${p.partner_id}`); }}>
                <div className="tk-card-head">
                  <div className="tk-card-avatar">{(p.unternehmen || "?")[0].toUpperCase()}</div>
                  <div className="tk-card-headtext">
                    <div className="tk-card-name">
                      {p.unternehmen}
                      {abgeschlossen && <span className="tk-card-tag">Abgeschlossen</span>}
                    </div>
                  </div>
                </div>

                <div className="tk-card-stats">
                  <div className="tk-stat">
                    <div className="tk-stat-label">Einreichungen</div>
                    <div className="tk-stat-value">{jfCounts[p.partner_id] || 0}</div>
                  </div>
                  <div className="tk-stat">
                    <div className="tk-stat-label">Versionen</div>
                    <div className="tk-stat-value">{e.anzahl}</div>
                  </div>
                  <div className="tk-stat">
                    <div className="tk-stat-label">Kosten</div>
                    <div className="tk-stat-value">{e.kosten > 0 ? formatEur(e.kosten) : "—"}</div>
                  </div>
                  <div className="tk-stat">
                    <div className="tk-stat-label">Zuletzt</div>
                    <div className="tk-stat-value tk-stat-value--soft">{formatDate(e.letztes)}</div>
                  </div>
                </div>

                <div className="tk-card-foot">
                  <span className="tk-card-foot-label">
                    {e.anzahl === 0
                      ? "Noch kein Portal"
                      : e.hinweise > 0
                        ? `${e.hinweise} offene Hinweise`
                        : "Keine offenen Hinweise"}
                  </span>
                  <button
                    className={`tk-card-icon-btn ${abgeschlossen ? "tk-card-icon-btn--restore" : ""}`}
                    onClick={(ev) => abschliessen(ev, p)}
                    data-tooltip={abgeschlossen ? "Wiederherstellen" : "Abschließen"}
                    aria-label={abgeschlossen ? "Wiederherstellen" : "Abschließen"}
                  >
                    {abgeschlossen ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {zeigeQuellen && (
        <DatenquellenModal onClose={() => { setZeigeQuellen(false); load(); }} />
      )}

      {neuOffen && (
        <PosteingangModal
          onClose={() => setNeuOffen(false)}
          onAngelegt={() => { setNeuOffen(false); load(); }}
        />
      )}
    </>
  );
}

// Welche Jotform-Formulare in die Recherche einfliessen. In der Datenbank
// gespeichert, damit die Auswahl ohne Serverzugriff aenderbar ist.
function DatenquellenModal({ onClose }) {
  const [quellen, setQuellen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [neueId, setNeueId] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/datenquellen", { credentials: "include" })
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Laden fehlgeschlagen");
        return d;
      })
      .then((d) => setQuellen(d.quellen || []))
      .catch((e) => setFehler(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const hinzufuegen = async () => {
    const id = neueId.trim();
    if (!id || busy) return;
    setBusy(true);
    setFehler("");
    try {
      const res = await fetch("/api/datenquellen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Hinzufügen fehlgeschlagen");
      setNeueId("");
      load();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  const sternSetzen = async (q) => {
    try {
      const res = await fetch(`/api/datenquellen/${encodeURIComponent(q.id)}/stern`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ stern: !q.stern }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Änderung fehlgeschlagen");
      load();
    } catch (e) {
      setFehler(e.message);
    }
  };

  const umschalten = async (q) => {
    try {
      await fetch(`/api/datenquellen/${encodeURIComponent(q.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ aktiv: !q.aktiv }),
      });
      load();
    } catch {
      setFehler("Änderung fehlgeschlagen.");
    }
  };

  const entfernen = async (q) => {
    if (!window.confirm(`Formular „${q.titel || q.id}“ nicht mehr als Quelle nutzen? Es wandert in den Papierkorb und lässt sich dort 30 Tage lang zurückholen.`)) return;
    try {
      await fetch(`/api/datenquellen/${encodeURIComponent(q.id)}`, {
        method: "DELETE", credentials: "include",
      });
      load();
    } catch {
      setFehler("Entfernen fehlgeschlagen.");
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h3>Datenquellen</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="karriere-erklaerung">
            Trag die Jotform-Formulare ein, aus denen für alle Partner recherchiert werden
            soll – die ID oder einfach die Adresse, beides funktioniert. Die Einreichungen
            werden automatisch dem Partner zugeordnet, dessen Firmenname darin vorkommt.
            Markiere mit dem Stern das Formular, das die Partner für ihr Karriereportal
            ausfüllen: Aus dessen Einreichungen entstehen die Portale. Ohne Stern lässt
            sich kein Portal anlegen.
          </p>

          <div className="chat-wizard-field">
            <label>Formular-ID hinzufügen</label>
            <div className="karriere-url-zeile">
              <input className="ai-prompt-input" placeholder="ID oder Adresse des Formulars"
                value={neueId} disabled={busy}
                onChange={(e) => { setNeueId(e.target.value); setFehler(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") hinzufuegen(); }} />
              <button className="btn-secondary-sm" onClick={hinzufuegen} disabled={!neueId.trim() || busy}>
                {busy ? <><Loader size={13} className="spin" /> Prüfe …</> : "Hinzufügen"}
              </button>
            </div>
            {fehler && <p className="karriere-scan-fehler">{fehler}</p>}
          </div>

          {loading ? (
            <p className="karriere-feld-hinweis"><Loader size={13} className="spin" /> Lade …</p>
          ) : (
            <div className="quellen-liste">
              {quellen.length === 0 ? (
                <div className="quellen-zeile">
                  <span className="quellen-titel karriere-feld-hinweis">
                    Noch keine weiteren Formulare verknüpft.
                  </span>
                </div>
              ) : quellen.map((q) => (
                <div key={q.id} className="quellen-zeile">
                  <input type="checkbox" checked={q.aktiv} onChange={() => umschalten(q)} />
                  <button className={`stern-knopf ${q.stern ? "stern-knopf--an" : ""}`}
                    title={q.stern ? "Grundlage für die Erstellung" : "Als Grundlage markieren"}
                    onClick={() => sternSetzen(q)}>
                    {q.stern ? "★" : "☆"}
                  </button>
                  <span className="quellen-titel">
                    {q.titel || q.id}
                    {q.stern && <span className="tk-card-tag">Grundlage</span>}
                    {q.fehler && <span className="karriere-scan-fehler">{q.fehler}</span>}
                  </span>
                  <span className="quellen-zahl">
                    {q.einreichungen != null ? `${q.einreichungen.toLocaleString("de-DE")} Einr.` : q.id}
                  </span>
                  <button className="chat-session-del" title="Entfernen" onClick={() => entfernen(q)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="modal-actions">
            <button className="btn-primary" onClick={onClose}>Fertig</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Ebene 2: die Portale eines Partners.
function PartnerPortale({ partner, onBack }) {
  const [portale, setPortale] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const segmente = useSegmente();
  const openId = segmente[0] === "careers" && segmente[2] ? segmente[2] : null;

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/karriereseiten", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const eigene = (Array.isArray(d) ? d : []).filter((k) => k.partner_id === partner.partner_id);
        // Version nach Entstehung vergeben: die aelteste ist Version 1.
        const nachAlter = [...eigene].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        const version = {};
        nachAlter.forEach((k, i) => { version[k.id] = i + 1; });
        // Anzeige: neueste zuerst.
        setPortale(nachAlter.reverse().map((k) => ({ ...k, version: version[k.id] })));
      })
      .catch(() => setPortale([]))
      .finally(() => setLoading(false));
  }, [partner.partner_id]);

  useEffect(() => { load(); }, [load]);

  // Eine Ueberarbeitung geht vom bestehenden Portal aus, nicht von einer neuen
  // Einreichung - die gibt es dabei ja gerade nicht.
  const neueVersion = async () => {
    const neueste = portale[0];
    if (!neueste) return;
    if (!window.confirm(
      `Neue Version auf Basis desselben Formulars anlegen (wie Version ${neueste.version})? `
      + "Angaben, Quellen und Differenzierungsmerkmale werden übernommen, die Abschnitte bleiben leer."
    )) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/karriereseiten/${neueste.id}/neue-version`, {
        method: "POST", credentials: "include",
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Anlegen fehlgeschlagen");
      navigate(`/careers/${partner.partner_id}/${d.id}`);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Dieses Karriereportal in den Papierkorb verschieben? Du kannst es dort 30 Tage lang wiederherstellen.")) return;
    try {
      const res = await fetch(`/api/karriereseiten/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error();
      load();
    } catch {
      window.alert("Löschen fehlgeschlagen.");
    }
  };

  if (openId) {
    return (
      <KarriereDetail
        id={openId}
        onBack={() => { navigate(`/careers/${partner.partner_id}`); load(); }}
      />
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Karriereportal"
        title={partner.unternehmen}
        subtitle={portale.length <= 1 ? "Karriereportal dieses Partners." : `${portale.length} Versionen des Karriereportals. Die neueste steht oben.`}
        actions={
          <>
            <button className="btn-secondary" onClick={onBack}><ArrowLeft size={16} /> Zurück</button>
            {portale.length > 0 ? (
              <>
                {/* Falls der Partner das Formular doch erneut einreicht: sonst
                    waere diese Einreichung nirgends mehr erreichbar, weil der
                    globale Posteingang Partner mit Portal ausblendet. */}
                <button className="icon-btn" onClick={() => setShowCreate(true)}
                  data-tooltip="Eingereichte Formulare" aria-label="Eingereichte Formulare">
                  <Inbox size={16} />
                </button>
                <button className="btn-primary" onClick={neueVersion} disabled={busy}>
                  {busy ? <><Loader size={16} className="spin" /> Lege an …</> : <><Plus size={16} /> Neue Version</>}
                </button>
              </>
            ) : (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                <Plus size={16} /> Portal anlegen
              </button>
            )}
          </>
        }
      />

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade …</span></div>
      ) : portale.length === 0 ? (
        <div className="empty-card card">
          <Globe size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>
            Noch kein Karriereportal für {partner.unternehmen}. Die Texte entstehen aus den
            Jotform-Formularen und der Website – du musst nichts abtippen.
          </p>
          <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Neues Portal
          </button>
        </div>
      ) : (
        <div className="cards-grid">
          {portale.map((s) => (
            <div key={s.id} className="tk-card uneq-card" role="button" tabIndex={0}
              onClick={() => navigate(`/careers/${partner.partner_id}/${s.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/careers/${partner.partner_id}/${s.id}`); }}>
              <div className="uneq-card-head">
                <h3>
                  Version {s.version}
                  {s.version === Math.max(...portale.map((x) => x.version)) && portale.length > 1
                    ? <span className="tk-card-tag">aktuell</span> : null}
                </h3>
                <button className="chat-session-del" title="Löschen"
                  onClick={(e) => loeschen(e, s.id)}><Trash2 size={14} /></button>
              </div>
              <div className="uneq-card-meta">
                <span className={"status-pill status-pill--" + (s.status || "entwurf") + " status-pill--readonly"}>
                  {STATUS_LABEL[s.status] || "Entwurf"}
                </span>
                <span className="uneq-card-farb">
                  {s.bloecke || 0} Blöcke
                  {Number(s.offene_hinweise) > 0 ? ` · ${s.offene_hinweise} Hinweise` : ""}
                </span>
              </div>
              <div className="uneq-card-foot">
                <span className="uneq-card-date">{formatDate(s.created_at)} · geändert {formatDate(s.updated_at)}</span>
                <span className="uneq-card-cost"><Coins size={12} /> {kosten(s)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <PosteingangModal
          partner={partner}
          onClose={() => setShowCreate(false)}
          onAngelegt={(neu) => { setShowCreate(false); navigate(`/careers/${partner.partner_id}/${neu.id}`); }}
        />
      )}
    </>
  );
}


// Einreichungen des Karriereportal-Formulars. Daraus entstehen die Portale -
// wie bei den Stellenanzeigen aus dem dortigen Formular.
function PosteingangModal({ partner, onClose, onAngelegt }) {
  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [fehler, setFehler] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const adresse = partner
      ? `/api/karriereportal/posteingang?partnerId=${encodeURIComponent(partner.partner_id)}`
      : "/api/karriereportal/posteingang";
    fetch(adresse, { credentials: "include" })
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Laden fehlgeschlagen");
        return d;
      })
      .then(setDaten)
      .catch((e) => setFehler(e.message))
      .finally(() => setLoading(false));
  }, [partner]);

  useEffect(() => { load(); }, [load]);

  // neueVersion: aus derselben Einreichung noch einmal. Nur in der Partnerkarte
  // erreichbar - im allgemeinen Posteingang waere das eine Doppelanlage.
  const anlegen = async (e, neueVersion = false) => {
    setBusy(e.submissionId);
    setFehler("");
    try {
      const res = await fetch(`/api/karriereportal/aus-einreichung/${encodeURIComponent(e.submissionId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ unternehmen: e.unternehmen, felder: e.felder, neueVersion }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Anlegen fehlgeschlagen");
      onAngelegt(d);
    } catch (err) {
      setFehler(err.message);
      setBusy("");
    }
  };

  return (
    <div className="modal-overlay" onClick={(ev) => ev.target === ev.currentTarget && !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 700 }}>
        <div className="modal-header">
          <h3>{partner ? `Einreichungen von ${partner.unternehmen}` : "Neues Projekt"}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <p className="karriere-feld-hinweis"><Loader size={13} className="spin" /> Lade …</p>
          ) : !daten || !daten.formular ? (
            <>
              <p className="karriere-erklaerung">
                Es ist noch kein Karriereportal-Formular festgelegt. Lege in den Datenquellen
                fest, welches Jotform-Formular die Partner für ihr Karriereportal ausfüllen –
                aus dessen Einreichungen entstehen dann die Portale.
              </p>
              <div className="modal-actions">
                <button className="btn-primary" onClick={onClose}>Verstanden</button>
              </div>
            </>
          ) : (
            <>
              <p className="karriere-erklaerung">
                {partner
                  ? `Einreichungen dieses Partners aus „${daten.formular.titel || daten.formular.extern_id}“. Aus jeder entsteht eine neue Version – auch aus derselben noch einmal.`
                  : `Aus „${daten.formular.titel || daten.formular.extern_id}“. Gezeigt werden nur Partner ohne Portal – Partner werden beim Anlegen automatisch erstellt, falls es sie noch nicht gibt.`}
              </p>
              {fehler && <div className="form-error">{fehler}</div>}
              {daten.einreichungen.length === 0 ? (
                <p className="karriere-feld-hinweis">
                  {partner
                    ? `Von ${partner.unternehmen} liegt keine Einreichung vor. Eine neue Version legst du dann über „Neue Version“ an – sie geht vom bestehenden Portal aus.`
                    : daten.ausgeblendet > 0
                      ? `Für alle ${daten.ausgeblendet} eingegangenen Formulare gibt es bereits ein Portal. Eine Überarbeitung legst du in der Karte des Partners als neue Version an.`
                      : "Noch keine Einreichungen."}
                </p>
              ) : (
                <div className="quellen-liste">
                  {daten.einreichungen.map((e) => (
                    <div key={e.submissionId} className="quellen-zeile">
                      <span className="quellen-titel">
                        <strong>{e.unternehmen}</strong>
                        <span className="quellen-zahl">{formatDate(e.eingereichtAm)}</span>
                      </span>
                      {e.portal && !partner ? (
                        <span className="quellen-zahl">Portal vorhanden</span>
                      ) : (
                        <button className="btn-secondary-sm" onClick={() => anlegen(e, !!e.portal)}
                          disabled={!!busy}>
                          {busy === e.submissionId
                            ? <><Loader size={13} className="spin" /> …</>
                            : e.portal ? "Neue Version aus diesem Formular" : "Portal anlegen"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!partner && daten.einreichungen.length > 0 && daten.ausgeblendet > 0 && (
                <p className="karriere-feld-hinweis">
                  {daten.ausgeblendet === 1
                    ? "1 Einreichung ist ausgeblendet – für diesen Partner gibt es bereits ein Portal."
                    : `${daten.ausgeblendet} Einreichungen sind ausgeblendet – für diese Partner gibt es bereits ein Portal.`}
                </p>
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={onClose}>Schließen</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function KarriereDetail({ id, onBack }) {
  const [seite, setSeite] = useState(null);
  const [fehler, setFehler] = useState("");
  const [busy, setBusy] = useState("");
  const [merkmale, setMerkmale] = useState([]);
  const [luecke, setLuecke] = useState("");
  const [verworfen, setVerworfen] = useState([]);
  const [offenesMerkmal, setOffenesMerkmal] = useState(null);

  const load = useCallback(() => {
    fetch(`/api/karriereseiten/${id}`, { credentials: "include" })
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Fehler beim Laden");
        return d;
      })
      .then((d) => { setSeite(d); setMerkmale(d.differenzierung || []); })
      .catch((e) => setFehler(e.message));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (fehler) return <div className="card users-error">{fehler}</div>;
  if (!seite) return <div className="card"><Loader size={16} className="spin" /> Lade …</div>;

  const bloecke = seite.sections || [];
  // Versteckte Abschnitte (Bewerbungsprozess, offene Stellen) zaehlen nicht mit -
  // sie werden nicht angezeigt und koennten sonst nie freigegeben werden.
  const pflichtOffen = bloecke.filter(
    (b) => b.pflicht && !b.automatisch && !b.versteckt && !b.freigegeben
  ).length;

  // --- Schritt 2: Differenzierungsmerkmale ---
  // Nur diese wandern in die Abschnitte: nicht verworfen und nicht leer.
  const uebernommene = merkmale.filter((m) => !m.verworfen && String(m.titel || "").trim());

  const merkmaleErzeugen = async () => {
    setBusy("diff");
    setFehler("");
    try {
      const res = await fetch(`/api/karriereseiten/${id}/differenzierung`, {
        method: "POST", credentials: "include",
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Fehler beim Ermitteln");
      // Selbst eingetragene Merkmale bleiben stehen. Die KI kennt sie nicht,
      // und sie waeren sonst mit einem Klick weg.
      setMerkmale([...merkmale.filter((m) => m.eigen), ...(d.merkmale || [])]);
      setLuecke(d.luecke || "");
      setVerworfen(Array.isArray(d.verworfen) ? d.verworfen : []);
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy("");
    }
  };

  const merkmaleFreigeben = async () => {
    setBusy("diff-ok");
    try {
      const res = await fetch(`/api/karriereseiten/${id}/differenzierung`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ merkmale: uebernommene }),
      });
      if (!res.ok) throw new Error("Freigeben fehlgeschlagen");
      load();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy("");
    }
  };

  const merkmalAendern = (i, feld, wert) =>
    setMerkmale(merkmale.map((m, j) => (i === j ? { ...m, [feld]: wert } : m)));

  // Jedes Merkmal einzeln entscheiden. Verworfene bleiben sichtbar, damit
  // nachvollziehbar ist, was geprueft wurde - freigegeben werden sie nicht.
  const merkmalUmschalten = (i) =>
    setMerkmale(merkmale.map((m, j) => (i === j ? { ...m, verworfen: !m.verworfen } : m)));

  const merkmalLoeschen = (i) => setMerkmale(merkmale.filter((_, j) => j !== i));

  // Ein eigenes Merkmal, ganz ohne KI. Du weisst oft mehr ueber den Partner,
  // als in den Quellen steht.
  const merkmalHinzufuegen = () =>
    setMerkmale([...merkmale, { titel: "", beleg: "", warum: "", quelle: "selbst ergänzt", eigen: true }]);

  // --- Schritt 3: Bloecke ---
  const blockVorbereiten = async (blockId) => {
    setBusy(blockId);
    setFehler("");
    try {
      const res = await fetch(`/api/karriereseiten/${id}/block/${blockId}`, {
        method: "POST", credentials: "include",
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Fehler beim Vorbereiten");
      load();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy("");
    }
  };

  // Schritt wechseln, ohne etwas zu verlieren: Quellen, Merkmale und Abschnitte
  // liegen alle in der Zeile - der Schritt sagt nur, was gerade angezeigt wird.
  const schrittWechseln = async (ziel) => {
    if (!seite || seite.schritt === ziel) return;
    setBusy("schritt");
    setFehler("");
    try {
      const res = await fetch(`/api/karriereseiten/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ schritt: ziel }),
      });
      if (!res.ok) throw new Error("Wechsel fehlgeschlagen");
      load();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy("");
    }
  };

  // Ausformulieren faehrt die ganze Kette: schreiben, pruefen, ueberarbeiten,
  // formatieren. Das dauert - deshalb ein eigener Busy-Zustand mit Erklaerung.
  const blockAusformulieren = async (blockId) => {
    setBusy(blockId + "-text");
    setFehler("");
    try {
      const res = await fetch(`/api/karriereseiten/${id}/block/${blockId}/ausformulieren`, {
        method: "POST", credentials: "include",
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Ausformulieren fehlgeschlagen");
      load();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy("");
    }
  };

  // Selbst geschriebener oder von Hand geaenderter Text. Laeuft ueber
  // denselben Endpunkt wie die Stichpunkte, nur mit einem anderen Feld.
  const blockTextSpeichern = async (blockId, text) => {
    setBusy(blockId + "-save");
    setFehler("");
    try {
      const res = await fetch(`/api/karriereseiten/${id}/block/${blockId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("Speichern fehlgeschlagen");
      load();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy("");
    }
  };

  const blockSpeichern = async (blockId, stichpunkte, freigegeben) => {
    setBusy(blockId + "-save");
    try {
      const res = await fetch(`/api/karriereseiten/${id}/block/${blockId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ stichpunkte, freigegeben }),
      });
      if (!res.ok) throw new Error("Speichern fehlgeschlagen");
      load();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Karriereportal"
        title={seite.unternehmen}
        subtitle={quellenText(seite)}
        actions={<button className="btn-secondary" onClick={onBack}><ArrowLeft size={16} /> Zurück</button>}
      />

      <div className="schritt-leiste">
        {/* Anklickbar: man muss zurueck zu den Quellen koennen, ohne den Stand
            zu verlieren - etwa um eine Website nachzutragen. */}
        {[
          { key: "quellen", nr: 1, label: "Quellen" },
          { key: "differenzierung", nr: 2, label: "Differenzierung" },
          { key: "bloecke", nr: 3, label: "Abschnitte", zusatz: pflichtOffen > 0 ? `${pflichtOffen} offen` : "" },
        ].map((sch) => {
          const aktiv = seite.schritt === sch.key || (sch.key === "bloecke" && seite.schritt === "fertig");
          const erledigt =
            (sch.key === "quellen" && seite.schritt !== "quellen") ||
            (sch.key === "differenzierung" && merkmale.length > 0 && seite.schritt === "bloecke");
          return (
            <button
              key={sch.key}
              type="button"
              className={`schritt schritt--knopf ${aktiv ? "schritt--aktiv" : erledigt ? "schritt--fertig" : ""}`}
              onClick={() => schrittWechseln(sch.key)}
              disabled={busy === "schritt"}
              title="Zu diesem Schritt wechseln"
            >
              <span className="schritt-nr">{erledigt ? <Check size={12} /> : sch.nr}</span>
              <span className="schritt-text">{sch.label}</span>
              {sch.zusatz && <span className="schritt-zusatz">{sch.zusatz}</span>}
            </button>
          );
        })}
      </div>

      {seite.schritt === "quellen" ? (
        <QuellenSchritt
          id={id}
          partnerId={seite.partner_id}
          onWeiter={() => load()}
        />
      ) : seite.schritt === "differenzierung" ? (
        <div className="card">
          <h3 className="dash-section-title">Die 3 Differenzierungsmerkmale</h3>
          <p className="karriere-erklaerung">
            Bevor ein Satz geschrieben wird: Womit kann dieser Arbeitgeber auftrumpfen?
            Gesucht ist Großes – lange Geschichte, viele Gewerke unter einem Dach, ein
            besonderer Ort, beeindruckende Projekte. Nicht „geregelter Notdienst“ oder
            „Firmenwagen“: das sind Arbeitsbedingungen, die kommen später. Grundlage sind
            die Website und das Karriereportal-Formular – die Website führt. Die
            Stellen-Formulare bleiben außen vor. Entscheide bei jedem Merkmal selbst
            und ergänze eigene.
          </p>

          {luecke && (
            <div className="block-fehlt">
              <strong>Um bessere Merkmale zu finden, fehlt:</strong> {luecke}
            </div>
          )}

          {verworfen.length > 0 && (
            <details className="karriere-verworfen">
              <summary>{verworfen.length} weitere Funde als zu klein aussortiert</summary>
              <ul>
                {verworfen.map((v, i) => (
                  <li key={i}><strong>{v.titel}</strong>{v.grund ? ` – ${v.grund}` : ""}</li>
                ))}
              </ul>
            </details>
          )}

          {merkmale.map((m, i) => {
            const offen = offenesMerkmal === i;
            return (
              <div className={`merkmal-zeile ${m.verworfen ? "merkmal-zeile--verworfen" : ""}`} key={i}>
                <div className="merkmal-kopf" role="button" tabIndex={0}
                  onClick={() => setOffenesMerkmal(offen ? null : i)}
                  onKeyDown={(e) => { if (e.key === "Enter") setOffenesMerkmal(offen ? null : i); }}>
                  {offen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="merkmal-titel">{m.titel || <em>ohne Titel</em>}</span>
                  <span className={`staerke staerke--${m.eigen ? "eigen" : m.staerke === "stark" ? "stark" : "schwach"}`}>
                    {m.eigen ? "von dir" : m.staerke === "stark" ? "trägt" : "zu klein"}
                  </span>
                  <span className="karriere-merkmal-spacer" />
                  <button
                    className={`tk-card-icon-btn ${m.verworfen ? "" : "tk-card-icon-btn--restore"}`}
                    onClick={(e) => { e.stopPropagation(); merkmalUmschalten(i); }}
                    data-tooltip={m.verworfen ? "Doch übernehmen" : "Verwerfen"}
                    aria-label={m.verworfen ? "Doch übernehmen" : "Verwerfen"}
                  >
                    {m.verworfen ? <Check size={14} /> : <X size={14} />}
                  </button>
                  <button className="tk-card-icon-btn"
                    onClick={(e) => { e.stopPropagation(); merkmalLoeschen(i); }}
                    data-tooltip="Löschen" aria-label="Löschen">
                    <Trash2 size={14} />
                  </button>
                </div>

                {!offen && m.beleg && <div className="merkmal-beleg-kurz">{m.beleg}</div>}

                {offen && (
                  <div className="merkmal-felder">
                    {m.quelle && !m.eigen && (
                      <div className="karriere-merkmal-quelle">Gefunden in: {m.quelle}</div>
                    )}
                    <label className="gate-feld-label">Merkmal</label>
                    <input className="ai-prompt-input" value={m.titel || ""}
                      placeholder="Merkmal in wenigen Worten"
                      onChange={(e) => merkmalAendern(i, "titel", e.target.value)} />
                    <label className="gate-feld-label">Beleg</label>
                    <textarea className="ai-prompt-input email-textarea" rows={2} value={m.beleg || ""}
                      placeholder="Der konkrete Beleg von der Website"
                      onChange={(e) => merkmalAendern(i, "beleg", e.target.value)} />
                    <label className="gate-feld-label">Warum das zählt</label>
                    <textarea className="ai-prompt-input email-textarea" rows={2} value={m.warum || ""}
                      placeholder="Warum das für Bewerber zählt"
                      onChange={(e) => merkmalAendern(i, "warum", e.target.value)} />
                  </div>
                )}
              </div>
            );
          })}

          <div className="modal-actions">
            <button className="btn-secondary" onClick={merkmalHinzufuegen}>
              <Plus size={16} /> Eigenes Merkmal
            </button>
            <button className="btn-secondary" onClick={merkmaleErzeugen} disabled={busy === "diff"}>
              {busy === "diff"
                ? <><Loader size={16} className="spin" /> Werte die Quellen aus …</>
                : <><Sparkles size={16} /> {merkmale.length ? "Neu ermitteln" : "Merkmale ermitteln"}</>}
            </button>
            <button className="btn-primary" onClick={merkmaleFreigeben}
              disabled={busy === "diff-ok" || uebernommene.length === 0}>
              {busy === "diff-ok"
                ? "Speichere …"
                : `${uebernommene.length} übernehmen – weiter zu den Abschnitten`}
            </button>
          </div>
          {merkmale.length > 0 && uebernommene.length === 0 && (
            <p className="karriere-feld-hinweis">
              Kein Merkmal übernommen. Behalte mindestens eines oder ergänze ein eigenes –
              ohne Merkmale haben die Abschnitte nichts, worauf sie sich beziehen können.
            </p>
          )}
          {fehler && <div className="form-error">{fehler}</div>}
        </div>
      ) : (
        <>
          <div className="card">
            <h3 className="dash-section-title">Die 3 Differenzierungsmerkmale</h3>
            {merkmale.map((m, i) => (
              <div className="karriere-hinweis" key={i}>
                <div><strong>{i + 1}. {m.titel}</strong></div>
                {m.beleg && <div className="karriere-problem">{m.beleg}</div>}
              </div>
            ))}
          </div>

          {fehler && <div className="card users-error">{fehler}</div>}

          <QuellenLeiste quellen={seite.quellen || []} briefing={seite.hatBriefing} />

          <KarriereBloecke
            bloecke={bloecke}
            busy={busy}
            onVorbereiten={blockVorbereiten}
            onSpeichern={blockSpeichern}
            onAusformulieren={blockAusformulieren}
            onTextSpeichern={blockTextSpeichern}
          />
        </>
      )}
    </>
  );
}

// Ein Abschnitt: Erklaerung aus dem Sheet, Stichpunkte der KI, Freigabe.
// Schritt 1: Quellen ergaenzen, bevor recherchiert wird.
function QuellenSchritt({ id, partnerId, onWeiter }) {
  const [quellen, setQuellen] = useState([]);
  const [typen, setTypen] = useState([]);
  const [neuTyp, setNeuTyp] = useState("website");
  const [neuWert, setNeuWert] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [check, setCheck] = useState(null);

  useEffect(() => {
    fetch(`/api/partners/${encodeURIComponent(partnerId)}/quellen`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { quellen: [], typen: [] }))
      .then((d) => { setQuellen(d.quellen || []); setTypen(d.typen || []); })
      .catch(() => {});
  }, [partnerId]);

  const speichern = async (liste) => {
    setBusy(true);
    setFehler("");
    try {
      const res = await fetch(`/api/partners/${encodeURIComponent(partnerId)}/quellen`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ quellen: liste }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Speichern fehlgeschlagen");
      setQuellen(d.quellen || []);
      return true;
    } catch (e) {
      setFehler(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const hinzufuegen = async () => {
    if (!neuWert.trim()) return;
    const ok = await speichern([...quellen, { typ: neuTyp, wert: neuWert.trim() }]);
    if (ok) setNeuWert("");
  };

  const aktuellerTyp = typen.find((t) => t.typ === neuTyp);

  return (
    <div className="card">
      <h3 className="dash-section-title">Schritt 1: Woraus soll recherchiert werden?</h3>
      <p className="karriere-erklaerung">
        Das eingereichte Formular ist bereits die Grundlage. Ergänze hier alles, was noch
        hilft – Website, Social-Media-Profil, weiteres Formular oder eigene Notizen. Diese
        Quellen gehören zum Partner und gelten für jedes seiner Portale. Je mehr Substanz,
        desto eher findet die KI echte Unterscheidungsmerkmale.
      </p>
      <p className="karriere-erklaerung">
        Mit <strong>Datencheck</strong> siehst du vorab, was die Quellen tatsächlich hergeben –
        aufgeteilt nach den Themen, die ein Karriereportal braucht. So merkst du vor dem
        Texten, wo etwas fehlt, statt es später am blassen Text zu erraten.
      </p>

      <div className="quellen-liste">
        {quellen.map((q, i) => (
          <div key={i} className="quellen-zeile">
            <span className="quellen-titel">
              <span className="tk-card-tag">{(typen.find((t) => t.typ === q.typ) || {}).label || q.typ}</span>
              {q.typ === "text" ? q.wert.slice(0, 80) + (q.wert.length > 80 ? " …" : "") : q.wert}
            </span>
            <button className="chat-session-del" title="Entfernen" disabled={busy}
              onClick={() => speichern(quellen.filter((_, j) => j !== i))}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="chat-wizard-field" style={{ marginTop: 16 }}>
        <label>Quelle ergänzen</label>
        <div className="karriere-url-zeile">
          <select className="sort-select" value={neuTyp} onChange={(e) => { setNeuTyp(e.target.value); setNeuWert(""); }}>
            {typen.filter((t) => t.eingabe).map((t) => (
              <option key={t.typ} value={t.typ}>{t.label}</option>
            ))}
          </select>
          {neuTyp === "text" ? (
            <textarea className="ai-prompt-input email-textarea" rows={3} value={neuWert}
              placeholder={aktuellerTyp ? aktuellerTyp.eingabe : ""}
              onChange={(e) => setNeuWert(e.target.value)} />
          ) : (
            <input className="ai-prompt-input" value={neuWert}
              placeholder={aktuellerTyp ? aktuellerTyp.eingabe : ""}
              onChange={(e) => setNeuWert(e.target.value)} />
          )}
          <button className="btn-secondary-sm" onClick={hinzufuegen} disabled={!neuWert.trim() || busy}>
            {busy ? <><Loader size={13} className="spin" /> …</> : "Ergänzen"}
          </button>
        </div>
        {fehler && <p className="karriere-scan-fehler">{fehler}</p>}
      </div>

      {check && (
        <div className="datencheck">
          <div className="datencheck-kopf">
            <strong>Das geben die Quellen her</strong>
            {check.gesamt && <span className="datencheck-gesamt">{check.gesamt}</span>}
          </div>

          {/* Welche hinterlegten Formulare wurden abgefragt? Ohne diese Liste
              sieht man nicht, ob ein Formular geprüft wurde oder ob es schlicht
              nichts hergab. */}
          {(check.bericht && check.bericht.formulare || []).length > 0 && (
            <div className="datencheck-formulare">
              <strong>Abgefragte Formulare</strong>
              {check.bericht.formulare.map((f, i) => (
                <div className={`datencheck-formular ${f.treffer > 0 ? "" : "datencheck-formular--leer"}`} key={i}>
                  <span>{f.titel}</span>
                  <span className="datencheck-formular-zahl">
                    {f.fehler
                      ? `Fehler: ${f.fehler}`
                      : f.treffer === 0
                        ? "keine Einreichung für diesen Partner"
                        : `${f.treffer} Einreichung${f.treffer === 1 ? "" : "en"}`}
                  </span>
                </div>
              ))}
            </div>
          )}
          {check.themen.map((t) => (
            <div className={`datencheck-thema datencheck-thema--${t.stand}`} key={t.id}>
              <div className="datencheck-thema-kopf">
                <span className="datencheck-punkt" />
                <strong>{t.label}</strong>
                <span className="datencheck-stand">
                  {t.stand === "gut" ? "reicht" : t.stand === "duenn" ? "dünn" : "nichts da"}
                </span>
              </div>
              {t.gefunden.length > 0 && (
                <ul className="datencheck-liste">
                  {t.gefunden.map((g, i) => <li key={i}>{g}</li>)}
                </ul>
              )}
              {t.fehlt.length > 0 && (
                <div className="datencheck-fehlt">
                  <strong>Beim Partner erfragen:</strong>
                  <ul>{t.fehlt.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="modal-actions">
        <button className="btn-secondary" disabled={busy} onClick={async () => {
          setBusy("check");
          setFehler("");
          try {
            const res = await fetch(`/api/karriereseiten/${id}/datencheck`, {
              method: "POST", credentials: "include",
            });
            const d = await res.json();
            if (!res.ok) throw new Error(d.error || "Datencheck fehlgeschlagen");
            setCheck(d);
          } catch (e) {
            setFehler(e.message);
          } finally {
            setBusy(false);
          }
        }}>
          {busy === "check"
            ? <><Loader size={16} className="spin" /> Sichte die Quellen …</>
            : <><Sparkles size={16} /> {check ? "Erneut sichten" : "Datencheck"}</>}
        </button>
        <button className="btn-primary" disabled={!!busy} onClick={async () => {
          setBusy(true);
          try {
            const res = await fetch(`/api/karriereseiten/${id}`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ schritt: "differenzierung" }),
            });
            if (!res.ok) throw new Error("Weiter fehlgeschlagen");
            onWeiter();
          } catch (e) {
            setFehler(e.message);
          } finally {
            setBusy(false);
          }
        }}>
          Weiter zu den Differenzierungsmerkmalen
        </button>
      </div>
    </div>
  );
}
